import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Loader, Tooltip } from "@vibe/core";
import { Add, Edit } from "@vibe/icons";
import { MondayApiQueryInstance } from "../../../services/monday/monday-service-ts";
import logger from "../../../lib/logger";
import type { ColumnValueType } from "../../../services/monday/monday-query-interface";
import { BuildResult, buildCheckboxValue, parseRawValue } from "./buildColumnValue";
import { isEditableColumnType, UNSUPPORTED_EDIT_MESSAGE } from "./columnEditability";
import { describeSaveError } from "./describeSaveError";
import { ColumnEditState, EditorProps } from "./editors/EditorFrame";
import { SingleInputEditor, LongTextEditor, TargetAndTextEditor } from "./editors/TextEditors";
import { DateEditor, HourEditor, TimelineEditor } from "./editors/DateEditors";
import {
  DropdownEditor,
  PeopleEditor,
  RatingEditor,
  StatusEditor,
  TagsEditor,
  OptionSkeleton,
} from "./editors/PickerEditors";

const EDITORS: Record<string, React.ComponentType<EditorProps>> = {
  text: (props) => <SingleInputEditor {...props} type="text" />,
  numbers: (props) => <SingleInputEditor {...props} type="numbers" />,
  phone: (props) => <SingleInputEditor {...props} type="phone" />,
  long_text: LongTextEditor,
  email: (props) => <TargetAndTextEditor {...props} type="email" />,
  link: (props) => <TargetAndTextEditor {...props} type="link" />,
  date: DateEditor,
  hour: HourEditor,
  timeline: TimelineEditor,
  status: StatusEditor,
  dropdown: DropdownEditor,
  tags: TagsEditor,
  people: PeopleEditor,
  rating: RatingEditor,
};

/**
 * Editors that open in a card floating under the value, so the row keeps its height. Single-line
 * values (text, numbers, email, link, phone) edit inline in a field the size of the cell.
 */
const POPOVER_WIDTHS: Record<string, number> = {
  status: 300,
  dropdown: 280,
  tags: 280,
  people: 280,
  date: 288,
  timeline: 288,
  hour: 260,
  long_text: 320,
};

/** The floating card. It opens below the value, or above when there isn't room below. */
const EditorPopover = ({
  width,
  cover,
  children,
}: {
  width: number;
  /** Sit over the value instead of under it. */
  cover?: boolean;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [above, setAbove] = useState(false);

  useLayoutEffect(() => {
    const rect = ref.current?.getBoundingClientRect();
    const anchor = ref.current?.parentElement?.getBoundingClientRect();
    if (
      !cover &&
      rect &&
      anchor &&
      rect.bottom > window.innerHeight &&
      anchor.top - rect.height - 4 >= 0
    ) {
      setAbove(true);
    }
  }, [cover]);

  return (
    <div
      ref={ref}
      data-testid="editor-popover"
      className={`absolute z-50 flex flex-col gap-1 p-3 bg-white rounded-lg !font-figtree text-[#323338] shadow-[0_6px_20px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.04)] ${
        cover ? "-top-3 -left-5" : above ? "bottom-full mb-1 left-0" : "top-full mt-1 left-0"
      }`}
      style={{ width, maxWidth: "calc(100vw - 32px)" }}
    >
      {children}
    </div>
  );
};

/** How often, and for how long, to look for a file the upload dialog is adding. */
export const UPLOAD_POLL_MS = 2000;
const UPLOAD_POLL_LIMIT_MS = 5 * 60 * 1000;

type Mode =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "editing"; state: ColumnEditState }
  | { kind: "saving" };

interface EditableColumnValueProps {
  columnType: string;
  columnId: string;
  boardId: number;
  itemId: number;
  /**
   * Called once a write lands (`saved`, with the column as monday returned it) or fails. The
   * spinner stays until it resolves, so show `written` at once and refetch in the background.
   */
  onSaved: (
    saved: boolean,
    written?: ColumnValueType,
    /** The column's parsed settings, as the editor read them (status label colours, …). */
    settings?: any,
  ) => Promise<unknown> | void;
  /** The column shows nothing; "Add value" stands in so there's something to click. */
  isEmpty?: boolean;
  /** The read-only rendering of the value. */
  children: ReactNode;
}

const showError = (message: string) =>
  MondayApiQueryInstance.mondayToastNotification(message, "error", 5000);

/**
 * Makes one of the current item's column values editable in place. Writes go to this item
 * only; SIMB's sync carries the change to the other boards. The display isn't updated
 * optimistically: after a write the view refetches, so a failed write leaves the old value.
 */
const EditableColumnValue = ({
  columnType,
  columnId,
  boardId,
  itemId,
  onSaved,
  isEmpty,
  children,
}: EditableColumnValueProps) => {
  const [mode, setMode] = useState<Mode>({ kind: "idle" });
  const [validationError, setValidationError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ReactNode>(null);
  // Enter saves and the editor's blur then fires as it unmounts; only the first may write.
  const writingRef = useRef(false);
  const mountedRef = useRef(true);
  // Opening the upload dialog again replaces the earlier watch rather than adding a second one.
  const uploadRunRef = useRef(0);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  if (!isEditableColumnType(columnType)) {
    return (
      <Tooltip content={UNSUPPORTED_EDIT_MESSAGE}>
        <div
          className="flex items-center w-full min-h-[28px] cursor-default"
          data-testid="readonly-column-value"
        >
          {children}
        </div>
      </Tooltip>
    );
  }

  const write = async (value: unknown, settings?: any) => {
    if (writingRef.current) return;
    writingRef.current = true;
    setMode({ kind: "saving" });
    let saved = false;
    let written: ColumnValueType | undefined;
    try {
      written = await MondayApiQueryInstance.changeColumnValue(boardId, itemId, columnId, value);
      saved = true;
    } catch (error: any) {
      logger.error("Error saving column value from item view", error);
      showError(describeSaveError(error));
    }
    try {
      await onSaved(saved, written, settings);
    } finally {
      writingRef.current = false;
      setPreview(null);
      setMode({ kind: "idle" });
    }
  };

  const loadState = async (): Promise<ColumnEditState | null> => {
    try {
      const raw = await MondayApiQueryInstance.getEditableColumnState(itemId, columnId);
      return {
        value: parseRawValue(raw.value),
        text: raw.text,
        settings: parseRawValue(raw.settingsStr) ?? {},
      };
    } catch (error: any) {
      logger.error("Error loading column value for editing", error);
      showError("Couldn't load this value for editing. Try again.");
      return null;
    }
  };

  const startEditing = async (event?: React.MouseEvent) => {
    // Links, emails and phone numbers in the value stay clickable, as on the board.
    if ((event?.target as HTMLElement | undefined)?.closest?.("a")) return;
    if (mode.kind !== "idle") return;
    setValidationError(null);
    setMode({ kind: "loading" });
    const state = await loadState();
    if (!state) return setMode({ kind: "idle" });

    // A checkbox has nothing to edit: a click flips it, as on the board.
    if (columnType === "checkbox") {
      const result = buildCheckboxValue(!state.value?.checked);
      return write(result.ok && result.value);
    }
    setMode({ kind: "editing", state });
  };

  const handleCommit = (result: BuildResult, nextDisplay?: ReactNode) => {
    if (writingRef.current) return;
    if (!result.ok) {
      setValidationError(result.error);
      return;
    }
    setValidationError(null);
    // Show the new value while it saves, instead of the old one.
    if (nextDisplay) setPreview(nextDisplay);
    void write(result.value, mode.kind === "editing" ? mode.state.settings : undefined);
  };

  const handleCancel = () => {
    setValidationError(null);
    setMode({ kind: "idle" });
  };

  const readFiles = async () =>
    (await MondayApiQueryInstance.getEditableColumnState(itemId, columnId)).value;

  /**
   * monday's upload dialog resolves when it opens, not when the file lands, so refreshing right
   * away showed nothing new. Watch the column instead and refresh once its files change.
   */
  const waitForUpload = async (before: string | null) => {
    const run = ++uploadRunRef.current;
    const deadline = Date.now() + UPLOAD_POLL_LIMIT_MS;
    const watching = () => mountedRef.current && run === uploadRunRef.current;
    while (watching() && Date.now() < deadline) {
      try {
        if ((await readFiles()) !== before) {
          if (watching()) await onSaved(true);
          return;
        }
      } catch (error: any) {
        logger.error("Error checking for an uploaded file", error);
      }
      await new Promise((resolve) => setTimeout(resolve, UPLOAD_POLL_MS));
    }
  };

  const uploadFile = async () => {
    let before: string | null;
    try {
      before = await readFiles();
      await MondayApiQueryInstance.triggerFilesUpload(boardId, itemId, columnId);
    } catch (error: any) {
      logger.error("Error opening the file upload dialog", error);
      showError("Couldn't open the file upload. Try again from the board.");
      return;
    }
    await waitForUpload(before);
  };

  if (columnType === "file") {
    return (
      <div className="flex gap-2 items-center w-full">
        {children}
        <Tooltip content="Add file">
          <button
            type="button"
            aria-label="Add file"
            className="flex items-center p-1 bg-transparent rounded border-0 cursor-pointer hover:bg-[#dcdfec]"
            onClick={uploadFile}
          >
            <Add size={16} color="#676879" />
          </button>
        </Tooltip>
      </div>
    );
  }

  const editing = mode.kind === "editing";
  const busy = mode.kind === "loading" || mode.kind === "saving";
  const popoverWidth = POPOVER_WIDTHS[columnType];
  // Long text's editor sits over the value instead of under it, so the text shows only once.
  const covers = columnType === "long_text";
  const Editor = EDITORS[columnType];
  const editor = mode.kind === "editing" && (
    <>
      <Editor
        state={mode.state}
        boardId={boardId}
        onCommit={handleCommit}
        onCancel={handleCancel}
      />
      {validationError && (
        <p role="alert" className="text-xs text-[#d83a52]">
          {validationError}
        </p>
      )}
    </>
  );

  const displayState = !editing
    ? "cursor-pointer hover:bg-[#dcdfec] focus-visible:bg-[#dcdfec]"
    : !popoverWidth
      ? "hidden"
      : covers
        ? "invisible"
        : "bg-[#dcdfec] shadow-[inset_0_0_0_1px_#0073ea]";

  // The same tree in every mode, so the value's renderer stays mounted. Remounting it after a
  // save made columns that look things up (people) show nothing until they had refetched.
  return (
    <div className="relative w-full" data-edit-root="">
      {/* The content stays set: with none, vibe drops the tooltip's wrapper, which remounts the
          value (blanking a dropdown's labels) and strands a tooltip that's showing. */}
      <Tooltip content="Click to edit" showTrigger={editing ? [] : undefined}>
        <div
          role="button"
          tabIndex={editing ? undefined : 0}
          aria-label={isEmpty ? "Add value" : "Edit value"}
          aria-busy={busy}
          data-testid="editable-column-value"
          className={`group flex gap-2 items-center w-full min-h-[32px] px-2 -mx-2 rounded focus-visible:outline-none ${displayState}`}
          // While a popover is open, pressing the value keeps focus in the editor rather than
          // closing it and opening it again.
          onMouseDown={(event) => editing && event.preventDefault()}
          onClick={startEditing}
          onKeyDown={(event) => {
            if (!editing && (event.key === "Enter" || event.key === " ")) {
              event.preventDefault();
              void startEditing();
            }
          }}
        >
          <div className="flex flex-1 items-center min-w-0">
            {preview}
            {/* Stays mounted under a preview, so it doesn't refetch when the preview goes. */}
            <div className={preview ? "hidden" : "contents"}>
              {isEmpty ? (
                <span className="text-light-secondary-text-color">Add value</span>
              ) : (
                children
              )}
            </div>
          </div>
          {busy && !(mode.kind === "loading" && popoverWidth && !covers) ? (
            <Loader size={16} />
          ) : (
            !editing && (
              <span
                aria-hidden="true"
                className="flex opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
              >
                <Edit size={16} color="#676879" />
              </span>
            )
          )}
        </div>
      </Tooltip>
      {mode.kind === "loading" && popoverWidth && !covers && (
        // Open the card at once; its choices fill in when the current value has loaded.
        <EditorPopover width={popoverWidth}>
          <OptionSkeleton avatars={columnType === "people"} />
        </EditorPopover>
      )}
      {editor &&
        (popoverWidth ? (
          <EditorPopover width={popoverWidth} cover={covers}>
            {editor}
          </EditorPopover>
        ) : (
          <div className="flex flex-col gap-1 w-full py-1">{editor}</div>
        ))}
    </div>
  );
};

export default EditableColumnValue;
